import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Save, Mail, Loader2, Send, Info } from 'lucide-react';

export function EmailSettings({ 
  data, 
  setData, 
  isSaving, 
  onSave, 
  IntegrationStatus,
  testEmail,
  setTestEmail,
  isSendingTest,
  onSendTestEmail,
  resendData,
  setResendData,
  onSaveResend,
  resendTestEmail,
  setResendTestEmail,
  isSendingResendTest,
  onSendResendTest
}) {
  return (
    <div className="space-y-6">
    {/* Resend (Custom Domain Transactional Email) */}
    <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="resend-settings-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins] flex items-center gap-2">
            <Send className="w-4 h-4 text-amber-600" />
            Resend (Transactional Email)
          </h3>
          <p className="text-sm text-slate-500 mt-1">Send all notifications, welcome emails and digests from your own verified domain</p>
        </div>
        <div className="flex items-center gap-3">
          <IntegrationStatus enabled={resendData?.enabled} />
          <Switch
            checked={!!resendData?.enabled}
            onCheckedChange={(checked) => setResendData(prev => ({ ...prev, enabled: checked }))}
            data-testid="resend-toggle"
          />
        </div>
      </div>

      <div className="flex gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl">
        <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          When enabled, all transactional emails are sent through <strong>your own Resend account</strong>, so the real
          <strong> From</strong> address is your verified domain (e.g. <strong>support@handybayani.com</strong>).
          The sender domain <strong>must be verified in Resend</strong> first. When disabled, emails fall back to the
          platform-managed sender (display name + reply-to only).
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Resend API Key</Label>
          <Input
            type="password"
            placeholder="re_••••••••••••••••"
            value={resendData?.api_key || ''}
            onChange={(e) => setResendData(prev => ({ ...prev, api_key: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="resend-api-key"
          />
          <p className="text-xs text-slate-400 mt-1">Create one at resend.com → API Keys. Leave unchanged to keep the saved key.</p>
        </div>
        <div>
          <Label>From Email</Label>
          <Input
            type="email"
            placeholder="support@handybayani.com"
            value={resendData?.from_email || ''}
            onChange={(e) => setResendData(prev => ({ ...prev, from_email: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="resend-from-email"
          />
          <p className="text-xs text-slate-400 mt-1">Must be on a domain verified in Resend</p>
        </div>
        <div>
          <Label>From Name (Sender Display Name)</Label>
          <Input
            placeholder="HandyBayani"
            value={resendData?.from_name || ''}
            onChange={(e) => setResendData(prev => ({ ...prev, from_name: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="resend-from-name"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Reply-To Email</Label>
          <Input
            type="email"
            placeholder="support@handybayani.com"
            value={resendData?.reply_to || ''}
            onChange={(e) => setResendData(prev => ({ ...prev, reply_to: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="resend-reply-to"
          />
        </div>
      </div>

      {/* Resend Test Section */}
      <div className="mt-6 p-4 bg-slate-50 rounded-xl">
        <h4 className="font-medium text-slate-900 mb-2">Test Resend Configuration</h4>
        <p className="text-sm text-slate-500 mb-3">Send a test email through your Resend account to verify the sender domain.</p>
        <div className="flex gap-3">
          <Input
            placeholder="Enter email to receive test"
            value={resendTestEmail}
            onChange={(e) => setResendTestEmail(e.target.value)}
            className="flex-1 h-10"
            data-testid="resend-test-email-input"
          />
          <Button
            onClick={onSendResendTest}
            disabled={isSendingResendTest || !resendTestEmail}
            variant="outline"
            className="h-10"
            data-testid="send-resend-test-btn"
          >
            {isSendingResendTest ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" />Send Test</>
            )}
          </Button>
        </div>
      </div>

      <Button
        onClick={onSaveResend}
        disabled={isSaving}
        className="mt-6 bg-slate-900 hover:bg-slate-800"
        data-testid="save-resend-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {isSaving ? 'Saving...' : 'Save Resend Settings'}
      </Button>
    </div>

    {/* Business SMTP (separate channel) */}
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins]">Business Email (SMTP) Settings</h3>
          <p className="text-sm text-slate-500 mt-1">Separate business SMTP server &mdash; test and diagnostics</p>
        </div>
        <div className="flex items-center gap-3">
          <IntegrationStatus enabled={data.enabled} />
          <Switch
            checked={data.enabled}
            onCheckedChange={(checked) => setData(prev => ({ ...prev, enabled: checked }))}
            data-testid="smtp-toggle"
          />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>SMTP Host</Label>
          <Input
            placeholder="smtp.gmail.com"
            value={data.host}
            onChange={(e) => setData(prev => ({ ...prev, host: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="smtp-host"
          />
        </div>
        <div>
          <Label>Port</Label>
          <Input
            type="number"
            placeholder="587"
            value={data.port}
            onChange={(e) => setData(prev => ({ ...prev, port: parseInt(e.target.value) }))}
            className="h-12 mt-1.5"
            data-testid="smtp-port"
          />
          <p className="text-xs text-slate-400 mt-1">Use 465 for SSL, 587 for TLS</p>
        </div>
        <div>
          <Label>Username</Label>
          <Input
            placeholder="your@email.com"
            value={data.username}
            onChange={(e) => setData(prev => ({ ...prev, username: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="smtp-username"
          />
        </div>
        <div>
          <Label>Password</Label>
          <Input
            type="password"
            placeholder="••••••••"
            value={data.password}
            onChange={(e) => setData(prev => ({ ...prev, password: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="smtp-password"
          />
        </div>
        <div className="md:col-span-2">
          <Label>From Email</Label>
          <Input
            placeholder="noreply@handybayani.com"
            value={data.from_email}
            onChange={(e) => setData(prev => ({ ...prev, from_email: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="smtp-from"
          />
        </div>
        <div className="md:col-span-2">
          <Label>From Name (Sender Display Name)</Label>
          <Input
            placeholder="HandyBayani"
            value={data.from_name}
            onChange={(e) => setData(prev => ({ ...prev, from_name: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="smtp-from-name"
          />
          <p className="text-xs text-slate-500 mt-1">This name will appear as the sender in all outgoing emails</p>
        </div>
        <div className="md:col-span-2">
          <Label>Reply-To Email</Label>
          <Input
            type="email"
            placeholder="support@handybayani.com"
            value={data.reply_to || ''}
            onChange={(e) => setData(prev => ({ ...prev, reply_to: e.target.value }))}
            className="h-12 mt-1.5"
            data-testid="smtp-reply-to"
          />
          <p className="text-xs text-slate-500 mt-1">When recipients hit "Reply", their response goes to this inbox. Leave blank to reply to the From address.</p>
        </div>
      </div>

      {/* Test Email Section */}
      <div className="mt-6 p-4 bg-slate-50 rounded-xl">
        <h4 className="font-medium text-slate-900 mb-2">Test Email Configuration</h4>
        <p className="text-sm text-slate-500 mb-3">Send a test email to verify your SMTP settings are working correctly.</p>
        <div className="flex gap-3">
          <Input
            placeholder="Enter email to receive test"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1 h-10"
            data-testid="test-email-input"
          />
          <Button 
            onClick={onSendTestEmail}
            disabled={isSendingTest || !testEmail}
            variant="outline"
            className="h-10"
            data-testid="send-test-email-btn"
          >
            {isSendingTest ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Send Test Email
              </>
            )}
          </Button>
        </div>
      </div>
      
      <Button 
        onClick={onSave}
        disabled={isSaving}
        className="mt-6 bg-slate-900 hover:bg-slate-800"
        data-testid="save-smtp-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {isSaving ? 'Saving...' : 'Save Email Settings'}
      </Button>
    </div>
    </div>
  );
}
